/**
 * Правила севооборота и журнал конфликтов (AK-767, решение founder Р34).
 *
 * Панель стоит РЯДОМ с ручным режимом, а не вместо него: правило раскидывает
 * культуры по тысячам полей, а поверх агроном правит руками столько, сколько
 * нужно. Поэтому здесь нет ни одного действия, которое переписывало бы уже
 * занятое поле, и каждое несостоявшееся назначение попадает в журнал вместо
 * того, чтобы промолчать.
 *
 * Порядок работы — «собрал → посмотрел → применил»
 * ------------------------------------------------
 * Заведение правила ничего не меняет в плане. Предпросмотр показывает, сколько
 * полей заденет каждое правило и где оно споткнётся. И только «Применить»
 * пишет. Разделение не формальность: одно нажатие трогает семь тысяч полей, и
 * увидеть это ДО записи — единственный способ не потерять день на откат.
 */
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useCropPicker } from '@/components/fields/card/api';
import { pickableCrops } from '../plan-variant/AddCropDialog';
import { formatHa } from '@/lib/planning/planVariant';
import {
  CONFLICT_TITLE,
  conflictSeverity,
  useApplyRotationRules,
  useCreateRotationRule,
  useDeleteRotationRule,
  usePlanRules,
  usePlanRulesPreview,
  type ConflictCode,
  type RotationRule,
  type RuleConflict,
  type RuleDraft,
  type ScopeLevel,
} from '@/lib/planning/rotationRules';
import { cn } from '@/lib/utils';

/** Radix Select не принимает пустую строку как значение — нужен свой маркер. */
const ANY = '__any__';
const FALLOW_KEY = 'fallow';

/** Сколько конфликтов показываем до «показать все» — журнал бывает на сотни строк. */
const CONFLICTS_SHOWN = 8;

interface CropOption {
  id: string;
  name: string;
}

interface Brigade {
  id: string;
  name: string;
}

function useBrigades(farmId: string | null) {
  return useQuery<Brigade[], Error>({
    queryKey: ['reference', 'brigades', farmId ?? 'none'],
    queryFn: () => api.get<Brigade[]>(`/api/v1/brigades?farm_id=${encodeURIComponent(farmId as string)}`),
    enabled: !!farmId,
    staleTime: 5 * 60_000,
  });
}

/**
 * Правило словами: «Озимая пшеница → Подсолнечник» либо цепочка кольца.
 *
 * Формулировка намеренно БЕЗ предлога «после»: по-русски он требует родительного
 * падежа («после озимой пшеницы»), а названия культур приезжают из справочника
 * в именительном. Склонять их кодом означало бы либо таблицу исключений на весь
 * справочник, либо «после озимая пшеница» на экране у агронома. Стрелка говорит
 * то же самое и не врёт грамматикой — падеж берёт на себя подпись колонки
 * «Предшественник».
 */
export function ruleTitle(rule: RotationRule): string {
  if (rule.kind === 'short') {
    const after = rule.after_crop_name ?? 'любой предшественник';
    const then = rule.then_is_fallow ? 'Пар' : (rule.then_crop_name ?? 'культура');
    return `${after} → ${then}`;
  }
  return rule.ring.map((p) => p.name).join(' → ');
}

/** Область применения словами агронома, а не уровнем модели. */
export function scopeTitle(rule: RotationRule): string {
  if (rule.scope_level === 'farm') return 'всё хозяйство';
  if (rule.scope_level === 'brigade') return `отделение ${rule.scope_brigade_name ?? '—'}`;
  return `выбранные поля: ${rule.scope_field_ids.length}`;
}

// ── Форма нового правила ────────────────────────────────────────────────────

function NewRuleForm({
  crops,
  brigades,
  disabled,
  onCreate,
}: {
  crops: CropOption[];
  brigades: Brigade[];
  disabled: boolean;
  onCreate: (draft: RuleDraft) => void;
}) {
  const [kind, setKind] = useState<'short' | 'ring'>('short');
  const [after, setAfter] = useState<string>(ANY);
  const [then, setThen] = useState<string>('');
  const [ring, setRing] = useState<string[]>([]);
  const [ringPick, setRingPick] = useState<string>('');
  const [scope, setScope] = useState<ScopeLevel>('farm');
  const [brigadeId, setBrigadeId] = useState<string>('');

  const cropName = (id: string) => (id === FALLOW_KEY ? 'Пар' : (crops.find((c) => c.id === id)?.name ?? 'культура'));
  const scopeReady = scope !== 'brigade' || !!brigadeId;
  const ready = scopeReady && (kind === 'short' ? then !== '' : ring.length >= 2);

  const submit = () => {
    if (!ready) return;
    const base: RuleDraft = {
      kind,
      scope_level: scope,
      scope_brigade_id: scope === 'brigade' ? brigadeId : null,
    };
    onCreate(
      kind === 'short'
        ? {
            ...base,
            after_crop_id: after === ANY ? null : after,
            then_crop_id: then === FALLOW_KEY ? null : then,
            then_is_fallow: then === FALLOW_KEY,
          }
        : { ...base, ring },
    );
    setThen('');
    setRing([]);
    setAfter(ANY);
  };

  return (
    <div className="space-y-3 rounded-md border border-border p-3">
      <div className="flex gap-2">
        {(['short', 'ring'] as const).map((k) => (
          <Button
            key={k}
            type="button"
            size="sm"
            variant={kind === k ? 'default' : 'outline'}
            aria-pressed={kind === k}
            onClick={() => setKind(k)}
          >
            {k === 'short' ? 'Короткое' : 'Кольцо'}
          </Button>
        ))}
      </div>

      {kind === 'short' ? (
        <div className="space-y-2">
          <div className="space-y-1">
            <Label className="text-xs">Если предшественник</Label>
            <Select value={after} onValueChange={setAfter}>
              <SelectTrigger className="h-8" aria-label="Предшественник в правиле">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>любой</SelectItem>
                {crops.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">→ сеем</Label>
            <Select value={then} onValueChange={setThen}>
              <SelectTrigger className="h-8" aria-label="Культура в правиле">
                <SelectValue placeholder="выберите культуру" />
              </SelectTrigger>
              <SelectContent>
                {crops.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
                {/* Пар в правиле разрешён: правило имеет право сказать «здесь
                    отдыхаем». Ставить его автоматом система всё равно не
                    станет — пар это состояние поля, а не культура (Р1). */}
                <SelectItem value={FALLOW_KEY}>Пар</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <Label className="text-xs">Кольцо по порядку</Label>
          <div className="flex flex-wrap items-center gap-1">
            {ring.length === 0 && (
              <span className="text-xs text-muted-foreground">
                добавьте хотя бы две позиции
              </span>
            )}
            {ring.map((key, i) => (
              <Badge key={`${key}-${i}`} variant="secondary" className="font-normal">
                {cropName(key)}
                <button
                  type="button"
                  className="ml-1 text-muted-foreground hover:text-foreground"
                  aria-label={`Убрать ${cropName(key)} из кольца`}
                  onClick={() => setRing(ring.filter((_, idx) => idx !== i))}
                >
                  ×
                </button>
              </Badge>
            ))}
          </div>
          <div className="flex gap-2">
            <Select value={ringPick} onValueChange={setRingPick}>
              <SelectTrigger className="h-8" aria-label="Позиция кольца">
                <SelectValue placeholder="добавить позицию" />
              </SelectTrigger>
              <SelectContent>
                {crops.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
                <SelectItem value={FALLOW_KEY}>Пар</SelectItem>
              </SelectContent>
            </Select>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!ringPick}
              onClick={() => {
                setRing([...ring, ringPick]);
                setRingPick('');
              }}
            >
              В кольцо
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-1">
        <Label className="text-xs">Применять к</Label>
        <div className="flex flex-wrap gap-1">
          {(
            [
              ['farm', 'всему хозяйству'],
              ['brigade', 'отделению'],
            ] as const
          ).map(([value, label]) => (
            <Button
              key={value}
              type="button"
              size="sm"
              variant={scope === value ? 'default' : 'outline'}
              aria-pressed={scope === value}
              onClick={() => setScope(value)}
            >
              {label}
            </Button>
          ))}
        </div>
        {scope === 'brigade' && (
          <Select value={brigadeId} onValueChange={setBrigadeId}>
            <SelectTrigger className="mt-1 h-8" aria-label="Отделение в правиле">
              <SelectValue placeholder="выберите отделение" />
            </SelectTrigger>
            <SelectContent>
              {brigades.map((b) => (
                <SelectItem key={b.id} value={b.id}>
                  {b.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <Button type="button" size="sm" className="w-full" disabled={!ready || disabled} onClick={submit}>
        Добавить правило
      </Button>
    </div>
  );
}

// ── Журнал конфликтов ───────────────────────────────────────────────────────

export function ConflictJournal({
  conflicts,
  loading = false,
}: {
  conflicts: RuleConflict[];
  /**
   * Пока раскладка считается, «конфликтов нет» говорить НЕЛЬЗЯ: пустой массив
   * при незагруженных данных и пустой массив при разобранных полях выглядят
   * одинаково, а значат противоположное. Незнание показываем как незнание.
   */
  loading?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Журнал конфликтов</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-12 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (conflicts.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Журнал конфликтов</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Правила разобрали все поля своей области — разбирать вручную нечего.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Группируем по ВИДУ конфликта, а не подряд списком: «предшественник
  // неизвестен у 9 полей» — это одно действие агронома, а девять одинаковых
  // строк подряд читаются как девять разных проблем.
  const groups = new Map<ConflictCode, RuleConflict[]>();
  for (const c of conflicts) {
    const bucket = groups.get(c.code) ?? [];
    bucket.push(c);
    groups.set(c.code, bucket);
  }
  const shown = expanded ? conflicts.length : CONFLICTS_SHOWN;
  let printed = 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-base">Журнал конфликтов</CardTitle>
        <Badge variant="secondary">{conflicts.length}</Badge>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Правило не смогло решить — эти поля разбираем руками.
        </p>
        {[...groups.entries()].map(([code, items]) => {
          const room = Math.max(0, shown - printed);
          const visible = items.slice(0, room);
          printed += visible.length;
          if (visible.length === 0) return null;
          return (
            <div key={code} className="space-y-1">
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'text-xs font-medium',
                    conflictSeverity(code) === 'blocking' ? 'text-destructive' : 'text-accent-brand',
                  )}
                >
                  {CONFLICT_TITLE[code]}
                </span>
                <span className="text-xs text-muted-foreground">{items.length}</span>
              </div>
              {visible.map((c) => (
                <div key={`${c.code}-${c.field_id}`} className="text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">{c.field_name}</span> — {c.message}
                </div>
              ))}
            </div>
          );
        })}
        {conflicts.length > CONFLICTS_SHOWN && (
          <Button type="button" size="sm" variant="ghost" onClick={() => setExpanded(!expanded)}>
            {expanded ? 'свернуть' : `показать все ${conflicts.length}`}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// ── Панель целиком ──────────────────────────────────────────────────────────

export function RotationRulesPanel({
  scenarioId,
  farmId,
  canSow,
}: {
  scenarioId: string;
  farmId: string | null;
  /** Право `sowings:C`. Без него правила видно, но не завести и не применить. */
  canSow: boolean;
}) {
  const [adding, setAdding] = useState(false);
  // Культуры берём тем же источником, что и остальной экран (каталог
  // хозяйства, а не весь справочник): правило, предлагающее культуру, которую
  // хозяйство не выращивает, — это правило, которое никогда не сработает.
  const cropPicker = useCropPicker();
  const crops: CropOption[] = pickableCrops(cropPicker.data ?? []);
  const rules = usePlanRules(scenarioId);
  const preview = usePlanRulesPreview(scenarioId);
  const brigades = useBrigades(farmId);
  const create = useCreateRotationRule(scenarioId);
  const remove = useDeleteRotationRule(scenarioId);
  const apply = useApplyRotationRules(scenarioId);

  const statsOf = (ruleId: string) => preview.data?.stats.find((s) => s.rule_id === ruleId);
  const willAssign = preview.data?.assignments.length ?? 0;
  const conflicts = preview.data?.conflicts ?? [];
  const list = rules.data ?? [];

  return (
    // Якорь для «Открыть правила севооборота» из мастера «По целям на
    // культуры» (AK-809): без него блок «правил нет» вёл бы в никуда.
    // `scroll-mt` — компенсация sticky-шапки, иначе цель уезжает под неё.
    <div id="rotation-rules-panel" className="scroll-mt-20 space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-base">Правила севооборота</CardTitle>
          {canSow && (
            <Button type="button" size="sm" variant="outline" onClick={() => setAdding(!adding)}>
              {adding ? 'Отмена' : '+ правило'}
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Применяются к группе полей; результат можно править руками поверх.
          </p>

          {adding && canSow && (
            <NewRuleForm
              crops={crops}
              brigades={brigades.data ?? []}
              disabled={create.isPending}
              onCreate={(draft) => {
                create.mutate(draft);
                setAdding(false);
              }}
            />
          )}

          {rules.isLoading && <Skeleton className="h-16 w-full" />}
          {!rules.isLoading && list.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Правил пока нет. Культуры расставляются вручную — или заведите правило,
              и система разнесёт их по истории полей.
            </p>
          )}

          {list.map((rule) => {
            const stats = statsOf(rule.id);
            return (
              <div key={rule.id} className="rounded-md border border-border bg-muted/30 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium">
                      {rule.kind === 'short' ? 'Короткое: ' : 'Кольцо: '}
                      {ruleTitle(rule)}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {scopeTitle(rule)}
                      {rule.created_by_name && ` · автор: ${rule.created_by_name}`}
                    </div>
                    {stats && (
                      <div className="mt-1 text-xs text-muted-foreground">
                        {stats.matched_fields > 0
                          ? `заденет полей: ${stats.matched_fields} · ${formatHa(stats.matched_area_ha)}`
                          : 'свободных полей под это правило нет'}
                        {stats.conflicts > 0 && (
                          <span className="text-accent-brand"> · конфликтов {stats.conflicts}</span>
                        )}
                      </div>
                    )}
                  </div>
                  {canSow && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(rule.id)}
                    >
                      Убрать
                    </Button>
                  )}
                </div>
              </div>
            );
          })}

          {canSow && list.length > 0 && (
            <div className="space-y-2 border-t border-border pt-3">
              <p className="text-xs text-muted-foreground">
                {/* Знаменатель — свободные поля, а не все: правило не трогает
                    занятые, и «заполнится 300 из 7 408» врало бы про работу,
                    которой оно не делает. */}
                Заполнится {willAssign} из {preview.data?.free_fields ?? 0} свободных полей
              </p>
              <Button
                type="button"
                size="sm"
                className="w-full"
                disabled={willAssign === 0 || apply.isPending || preview.isLoading}
                onClick={() => apply.mutate({})}
              >
                {apply.isPending ? 'Применяем…' : 'Применить правила'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <ConflictJournal conflicts={conflicts} loading={preview.isLoading} />
    </div>
  );
}
